import React, { useEffect, useState } from 'react';
import { Archive, Plus, Trash2 } from 'lucide-react';
import Card from '../components/Card';
import Modal from '../components/Modal';
import { SkeletonDetail } from '../components/Skeleton';
import { useToast } from '../components/ToastProvider';
import { useAuth } from '../context/AuthContext';
import DataSchemaEditor from '../features/admin/schemas/DataSchemaEditor';
import SchemaRail from '../features/admin/schemas/SchemaRail';
import useDataSchemas from '../features/admin/schemas/useDataSchemas';
import { getSchemaMetaSchema } from '../features/structured-data/structuredDataApi';
import useEnumOptions from '../features/structured-data/useEnumOptions';
import useUnsavedChangesGuard from '../hooks/useUnsavedChangesGuard';

// The meta-schema only changes on deploy — one fetch per session is plenty.
let metaSchemaCache = null;

// Admin > Data Schemas — reusable extraction schemas the AI fills per contract.
// Master/detail: schema rail on the left (live schemas, then a collapsed
// Archived section — doc-89); the right column is ONE card per schema
// (doc-105) — a sticky header (name, chips, Save/Reset, meta line) over three
// job-mapped tabs: Definition, Settings, Test & run.
export default function DataSchemas() {
  const { showToast } = useToast();
  const { can } = useAuth();
  const ctl = useDataSchemas({ showToast });
  const { typeOptions, categoryOptions } = useEnumOptions();
  useUnsavedChangesGuard(ctl.anyDirty);

  const [metaSchema, setMetaSchema] = useState(metaSchemaCache);
  useEffect(() => {
    if (metaSchemaCache) return undefined;
    let cancelled = false;
    getSchemaMetaSchema()
      .then((doc) => {
        metaSchemaCache = doc;
        if (!cancelled) setMetaSchema(doc);
      })
      // The client validator falls back to its shipped constants.
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Archive and delete confirm here, raised from the editor's Settings tab
  // (doc-108 — the rail is pure navigation); restore is one click there.
  // { kind: 'archive' | 'delete', id, name } — outputCount is read live from
  // the schema list so the delete dialog can offer archive-instead.
  const [confirm, setConfirm] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const confirmSchema = confirm ? ctl.byId.get(confirm.id) : null;
  const confirmOutputs = confirmSchema?.outputCount ?? 0;

  const closeConfirm = () => { if (!confirmBusy) setConfirm(null); };
  const runConfirm = async (action) => {
    setConfirmBusy(true);
    const ok = await action();
    setConfirmBusy(false);
    if (ok) setConfirm(null);
  };

  const draft = ctl.selectedId ? ctl.draftFor(ctl.selectedId) : null;
  const base = ctl.selectedId ? (ctl.byId.get(ctl.selectedId) || null) : null;
  const isNew = ctl.selectedId ? ctl.isNewId(ctl.selectedId) : false;

  return (
    <div className="canvas-inner animate-fade-in">
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Admin</div>
          <h1 className="page-title">Data Schemas</h1>
          <div className="page-subtitle">
            Reusable extraction schemas the AI fills per contract — define the fields once, run from
            a contract's Structured Data tab.
          </div>
        </div>
      </div>

      {ctl.loading ? (
        <SkeletonDetail />
      ) : ctl.error ? (
        <div className="callout red"><div className="callout-body">{ctl.error}</div></div>
      ) : (
        <div className="schema-workbench">
          <SchemaRail
            items={ctl.railItems}
            archivedItems={ctl.archivedItems}
            selectedId={ctl.selectedId}
            isDirty={ctl.isDirty}
            onSelect={ctl.setSelectedId}
            onAddNew={ctl.addNew}
          />

          {/* Selected schema */}
          {draft ? (
            /* Keyed by selection: per-schema editor state (JSON text, AI pane,
               export flags, test-run picks) must reset on a rail switch or it
               shows the previous schema's state (the doc-49 class of bug). */
            <DataSchemaEditor
              key={ctl.selectedId}
              draft={draft}
              base={base}
              isNew={isNew}
              isDirty={ctl.isDirty(ctl.selectedId)}
              models={ctl.models}
              typeOptions={typeOptions}
              categoryOptions={categoryOptions}
              metaSchema={metaSchema}
              canRunBatches={can('run_schemas')}
              canExport={can('write_contracts')}
              canManageAutomation={can('manage_settings')}
              onSaveAutomation={(enabled) => ctl.setAutomation(base.id, enabled)}
              saving={ctl.saving}
              onPatch={(patch) => ctl.setDraft(ctl.selectedId, patch)}
              onSave={() => ctl.save(ctl.selectedId)}
              onReset={() => ctl.reset(ctl.selectedId)}
              onArchive={() => setConfirm({ kind: 'archive', id: ctl.selectedId, name: draft.name })}
              onRestore={() => ctl.setActive(ctl.selectedId, true)}
              onDelete={() => setConfirm({ kind: 'delete', id: ctl.selectedId, name: draft.name })}
            />
          ) : (
            <Card title="Data Schemas">
              <div style={{ padding: 40, textAlign: 'center' }}>
                <div className="muted" style={{ fontSize: 13 }}>
                  No data schemas yet. Create one to start extracting structured data from contracts.
                </div>
                <button type="button" className="btn primary sm" style={{ marginTop: 12 }} onClick={ctl.addNew}>
                  <Plus className="w-3.5 h-3.5" />
                  New schema
                </button>
              </div>
            </Card>
          )}
        </div>
      )}

      <Modal
        isOpen={confirm?.kind === 'archive'}
        onClose={closeConfirm}
        title="Archive schema"
        icon={Archive}
        size="sm"
        footer={(
          <>
            <button type="button" className="btn outline sm" onClick={closeConfirm} disabled={confirmBusy}>Cancel</button>
            <button
              type="button"
              className="btn primary sm"
              disabled={confirmBusy}
              onClick={() => runConfirm(() => ctl.setActive(confirm.id, false))}
            >
              {confirmBusy ? 'Archiving…' : 'Archive'}
            </button>
          </>
        )}
      >
        <div className="card-body" style={{ fontSize: 13, lineHeight: 1.55 }}>
          Archive <strong>{confirm?.name || 'this schema'}</strong>? It disappears from contract pages and
          can't be run — batch runs skip it too. Extracted outputs stay readable, labeled "Schema off".
          You can restore it anytime — select it under Archived in the schema list, then
          Settings → Restore schema. Deleting, by contrast, is permanent.
        </div>
      </Modal>

      <Modal
        isOpen={confirm?.kind === 'delete'}
        onClose={closeConfirm}
        title="Delete schema"
        icon={Trash2}
        size="sm"
        footer={(
          <>
            <button type="button" className="btn outline sm" onClick={closeConfirm} disabled={confirmBusy}>Cancel</button>
            {confirmOutputs > 0 ? (
              <button
                type="button"
                className="btn primary sm"
                disabled={confirmBusy || confirmSchema?.active === false}
                onClick={() => runConfirm(() => ctl.setActive(confirm.id, false))}
              >
                {confirmBusy ? 'Archiving…' : 'Archive instead'}
              </button>
            ) : (
              <button
                type="button"
                className="btn danger sm"
                disabled={confirmBusy}
                onClick={() => runConfirm(() => ctl.remove(confirm.id))}
              >
                {confirmBusy ? 'Deleting…' : 'Delete schema'}
              </button>
            )}
          </>
        )}
      >
        <div className="card-body" style={{ fontSize: 13, lineHeight: 1.55 }}>
          {confirmOutputs > 0 ? (
            <>
              <strong>{confirm?.name || 'This schema'}</strong> has {confirmOutputs} extracted
              output{confirmOutputs === 1 ? '' : 's'} across contracts, so it can't be deleted — deleting
              would orphan that data. Archiving hides the schema and stops runs while existing outputs stay
              readable{confirmSchema?.active === false ? ' — it is already archived' : ''}.
            </>
          ) : (
            <>
              Permanently delete <strong>{confirm?.name || 'this schema'}</strong>? This also removes its
              batch-run history and cannot be undone. If you might need it again, archive it instead —
              archived schemas can be restored.
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
